import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Grid, Divider } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useAuth } from '../../../../contexts/AuthContext';

const EmployeeEmergencyInfoForm = ({ language = 'en', onSubmit, loading, disabled }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '', homeAddress: '', city: '', postalCode: '', homePhone: '', cellularPhone: '',
    emailAddress: '', socialInsuranceNumber: '', dateOfBirth: '', doctorName: '', doctorPhone: '',
    medicalConditions: '', allergies: '', currentMedications: '', emergencyContactName: '',
    relationship: '', emergencyAddress: '', emergencyPhone: '', emergencyPhone2: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
      setFormData(prev => ({ ...prev, name: fullName, emailAddress: user.email || '' }));
    }
  }, [user]);

  const labels = language === 'es' ? {
    name: 'Nombre', homeAddress: 'Dirección', city: 'Ciudad', postalCode: 'Código Postal',
    homePhone: 'Teléfono de Casa', cellularPhone: 'Teléfono Celular', emailAddress: 'Correo Electrónico',
    socialInsuranceNumber: 'Número de Seguro Social', dateOfBirth: 'Fecha de Nacimiento',
    doctorName: 'Nombre del Médico', doctorPhone: 'Teléfono del Médico',
    medicalConditions: 'Condiciones Médicas', allergies: 'Alergias', currentMedications: 'Medicamentos Actuales',
    emergencyContact: 'Contacto de Emergencia', emergencyContactName: 'Nombre del Contacto de Emergencia',
    relationship: 'Relación', emergencyAddress: 'Dirección', emergencyPhone: 'Teléfono',
    save: 'Guardar', required: 'Obligatorio'
  } : {
    name: 'Name', homeAddress: 'Home Address', city: 'City', postalCode: 'Postal Code',
    homePhone: 'Home Phone', cellularPhone: 'Cellular Phone', emailAddress: 'Email Address',
    socialInsuranceNumber: 'Social Insurance Number', dateOfBirth: 'Date of Birth',
    doctorName: "Doctor's Name", doctorPhone: "Doctor's Phone",
    medicalConditions: 'Medical Conditions', allergies: 'Allergies', currentMedications: 'Current Medications',
    emergencyContact: 'Emergency Contact', emergencyContactName: "Emergency Contact's Name",
    relationship: 'Relationship', emergencyAddress: 'Address', emergencyPhone: 'Phone Number',
    save: 'Save Form', required: 'Required'
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    ['name', 'homeAddress', 'city', 'postalCode', 'homePhone', 'emailAddress', 'emergencyContactName', 'relationship', 'emergencyAddress', 'emergencyPhone'].forEach(field => {
      if (!formData[field].trim()) newErrors[field] = labels.required;
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      onSubmit(formData, null);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>Personal Information</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={labels.name} name="name" value={formData.name} onChange={handleChange} required disabled={disabled} error={!!errors.name} helperText={errors.name} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={labels.homeAddress} name="homeAddress" value={formData.homeAddress} onChange={handleChange} required disabled={disabled} error={!!errors.homeAddress} helperText={errors.homeAddress} /></Grid>
        <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth label={labels.city} name="city" value={formData.city} onChange={handleChange} required disabled={disabled} error={!!errors.city} helperText={errors.city} /></Grid>
        <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth label={labels.postalCode} name="postalCode" value={formData.postalCode} onChange={handleChange} required disabled={disabled} error={!!errors.postalCode} helperText={errors.postalCode} /></Grid>
        <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth label={labels.homePhone} name="homePhone" value={formData.homePhone} onChange={handleChange} required disabled={disabled} error={!!errors.homePhone} helperText={errors.homePhone} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={labels.cellularPhone} name="cellularPhone" value={formData.cellularPhone} onChange={handleChange} disabled={disabled} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={labels.emailAddress} name="emailAddress" type="email" value={formData.emailAddress} onChange={handleChange} required disabled={disabled} error={!!errors.emailAddress} helperText={errors.emailAddress} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={labels.socialInsuranceNumber} name="socialInsuranceNumber" value={formData.socialInsuranceNumber} onChange={handleChange} disabled={disabled} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={labels.dateOfBirth} name="dateOfBirth" type="date" value={formData.dateOfBirth} onChange={handleChange} disabled={disabled} InputLabelProps={{ shrink: true }} /></Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>Medical Information</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={labels.doctorName} name="doctorName" value={formData.doctorName} onChange={handleChange} disabled={disabled} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={labels.doctorPhone} name="doctorPhone" value={formData.doctorPhone} onChange={handleChange} disabled={disabled} /></Grid>
        <Grid size={12}><TextField fullWidth multiline rows={2} label={labels.medicalConditions} name="medicalConditions" value={formData.medicalConditions} onChange={handleChange} disabled={disabled} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={labels.allergies} name="allergies" value={formData.allergies} onChange={handleChange} disabled={disabled} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={labels.currentMedications} name="currentMedications" value={formData.currentMedications} onChange={handleChange} disabled={disabled} /></Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>{labels.emergencyContact}</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={labels.emergencyContactName} name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange} required disabled={disabled} error={!!errors.emergencyContactName} helperText={errors.emergencyContactName} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={labels.relationship} name="relationship" value={formData.relationship} onChange={handleChange} required disabled={disabled} error={!!errors.relationship} helperText={errors.relationship} /></Grid>
        <Grid size={12}><TextField fullWidth label={labels.emergencyAddress} name="emergencyAddress" value={formData.emergencyAddress} onChange={handleChange} required disabled={disabled} error={!!errors.emergencyAddress} helperText={errors.emergencyAddress} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={labels.emergencyPhone} name="emergencyPhone" value={formData.emergencyPhone} onChange={handleChange} required disabled={disabled} error={!!errors.emergencyPhone} helperText={errors.emergencyPhone} /></Grid>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={`${labels.emergencyPhone} 2`} name="emergencyPhone2" value={formData.emergencyPhone2} onChange={handleChange} disabled={disabled} /></Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="submit" variant="contained" size="large" startIcon={<SaveIcon />} disabled={disabled || loading}>
          {language === 'es' ? 'Guardar' : 'Save Form'}
        </Button>
      </Box>
    </Box>
  );
};

export default EmployeeEmergencyInfoForm;

